/**
 * DriftBadge — pulses red on the HQ tab when code/plan disagree.
 *
 * Polls `/api/aria/projects/{projectId}/drift-status` every 30s and shows
 * a small red dot + count when `has_drift === true`. Click → optional
 * callback (e.g. open the Loop where Aria's drift question lives).
 */
import React, { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function DriftBadge({ projectId, onClick, inline = false }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    const fetchStatus = async () => {
      try {
        const r = await fetch(`${API}/api/aria/projects/${projectId}/drift-status`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setCount(data.drift_count || 0);
      } catch { /* silent */ }
    };
    fetchStatus();
    const t = setInterval(fetchStatus, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, [projectId]);

  if (count === 0) return null;

  return (
    <button
      data-testid="drift-badge"
      onClick={onClick}
      title={`${count} drift(s) detected · code and plan disagree`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: inline ? '2px 8px' : '4px 10px',
        background: '#FEE2E2',
        border: '1px solid #FCA5A5',
        borderRadius: 999,
        cursor: onClick ? 'pointer' : 'default',
        fontSize: 10, fontWeight: 800,
        letterSpacing: '0.14em', textTransform: 'uppercase',
        color: '#B91C1C',
        animation: 'driftPulse 2s ease-in-out infinite',
      }}
    >
      <span style={{
        width: 6, height: 6, borderRadius: '50%',
        background: '#EF4444',
        animation: 'driftDotPulse 1.2s ease-in-out infinite',
      }} />
      {count} drift{count > 1 ? 's' : ''}
      <style>{`
        @keyframes driftPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.4); }
          50%       { box-shadow: 0 0 0 6px rgba(239,68,68,0); }
        }
        @keyframes driftDotPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.55; transform: scale(0.85); }
        }
      `}</style>
    </button>
  );
}
